import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Alert, { AlertDescription } from '../components/ui/alert';
import { RefreshCw, AlertCircle, ChevronDown, Clock, Filter } from 'lucide-react';

// The main improvements:
// 1. Added filtering capabilities for country data
// 2. Improved responsive design for mobile
// 3. Limited data points shown in mobile view
// 4. Added toggle capabilities for charts
// 5. Improved chart configurations for better mobile display

interface CountryData {
  country: string;
  count: number;
  total_cost: string;
  avg_processing_time: string;
}

interface MetricsData {
  totalRequests: Array<{ total: number }>;
  lastHourRequests: Array<{ total: number }>;
  lastDayRequests: Array<{ total: number }>;
  lastWeekRequests: Array<{ total: number }>;
  lastMonthRequests: Array<{ total: number }>;
  costByDay: Array<{ date: string; total_cost: number }>;
  requestsByIp: Array<{ ip_address: string; count: number }>;
  requestsByCountry: Array<CountryData>;
  requestsByEndpoint: Array<{ endpoint: string; count: number }>;
  requestsByInputType: Array<{ input_type: string; count: number; total_cost: string }>;
  avgProcessingTime: Array<{ endpoint: string; avg_time: number }>;
  hourlyStats: Array<{
    hour: number;
    request_count: number;
    total_cost: string;
    avg_processing_time: number;
  }>;
}

interface ChartCardProps {
  title: string;
  id: string;
  children: React.ReactNode;
  className?: string;
  filterComponent?: React.ReactNode;
}

interface MultiPeriodCountryData {
  country: string;
  daily_cost: number;
  weekly_cost: number;
  monthly_cost: number;
  daily_count: number;
  weekly_count: number;
  monthly_count: number;
}

const Dashboard = () => {
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [dailyCountryData, setDailyCountryData] = useState<CountryData[]>([]);
  const [weeklyCountryData, setWeeklyCountryData] = useState<CountryData[]>([]);
  const [monthlyCountryData, setMonthlyCountryData] = useState<CountryData[]>([]);
  const [multiPeriodCountryData, setMultiPeriodCountryData] = useState<MultiPeriodCountryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set(['costs']));
  const [isMobile, setIsMobile] = useState(false);
  
  // Filtering states
  const [countryFilter, setCountryFilter] = useState('count');
  const [countryLimit, setCountryLimit] = useState(10);
  const [hourlyChartType, setHourlyChartType] = useState('both');
  const [timePeriod, setTimePeriod] = useState('month');
  const [countryCostLimit, setCountryCostLimit] = useState(10);
  const [countryCostSortBy, setCountryCostSortBy] = useState('monthly_cost');
  
  // Filtered data states
  const [filteredCountries, setFilteredCountries] = useState<any[]>([]);
  const [filteredMultiPeriodCountries, setFilteredMultiPeriodCountries] = useState<MultiPeriodCountryData[]>([]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      // Keep all cards expanded by default
      setExpandedCards(new Set(['costs', 'hourly', 'ips', 'endpoints', 'input-types', 'processing', 'countries', 'country-costs']));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await fetch(`https://api.notely.se/dashboard/metrics?timePeriod=${timePeriod}`);
        if (!response.ok) throw new Error('Failed to fetch metrics');
        const data = await response.json();
        
        // Reverse the order of costByDay data to show latest date on the right
        if (data.costByDay && Array.isArray(data.costByDay)) {
          data.costByDay = [...data.costByDay].reverse();
        }
        
        setMetrics(data as MetricsData);
        setLastUpdated(new Date());
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 300000);
    return () => clearInterval(interval);
  }, [timePeriod]);

  // Fetch multi-period country cost data
  useEffect(() => {
    const fetchMultiPeriodCountryData = async () => {
      try {
        // Fetch all three time periods in parallel
        const [dailyResponse, weeklyResponse, monthlyResponse] = await Promise.all([
          fetch('https://api.notely.se/dashboard/metrics?timePeriod=day'),
          fetch('https://api.notely.se/dashboard/metrics?timePeriod=week'),
          fetch('https://api.notely.se/dashboard/metrics?timePeriod=month')
        ]);

        if (!dailyResponse.ok || !weeklyResponse.ok || !monthlyResponse.ok) {
          throw new Error('Failed to fetch country cost data');
        }

        const [dailyData, weeklyData, monthlyData] = await Promise.all([
          dailyResponse.json(),
          weeklyResponse.json(),
          monthlyResponse.json()
        ]);

        // Store individual datasets
        if (dailyData.requestsByCountry) setDailyCountryData(dailyData.requestsByCountry);
        if (weeklyData.requestsByCountry) setWeeklyCountryData(weeklyData.requestsByCountry);
        if (monthlyData.requestsByCountry) setMonthlyCountryData(monthlyData.requestsByCountry);

      } catch (err) {
        console.error('Error fetching multi-period country cost data:', err);
      }
    };

    fetchMultiPeriodCountryData();
    const interval = setInterval(fetchMultiPeriodCountryData, 300000);
    return () => clearInterval(interval);
  }, []);

  // Combine multi-period data into unified structure
  useEffect(() => {
    if (dailyCountryData.length === 0 || weeklyCountryData.length === 0 || monthlyCountryData.length === 0) {
      return;
    }

    // Create maps for quick lookup
    const dailyMap = new Map(dailyCountryData.map(item => [item.country, item]));
    const weeklyMap = new Map(weeklyCountryData.map(item => [item.country, item]));
    const monthlyMap = new Map(monthlyCountryData.map(item => [item.country, item]));

    // Get all unique countries
    const allCountries = new Set([
      ...dailyCountryData.map(item => item.country),
      ...weeklyCountryData.map(item => item.country),
      ...monthlyCountryData.map(item => item.country)
    ]);

    // Combine data
    const combined: MultiPeriodCountryData[] = Array.from(allCountries).map(country => {
      const daily = dailyMap.get(country);
      const weekly = weeklyMap.get(country);
      const monthly = monthlyMap.get(country);

      return {
        country,
        daily_cost: daily ? parseFloat(daily.total_cost) : 0,
        weekly_cost: weekly ? parseFloat(weekly.total_cost) : 0,
        monthly_cost: monthly ? parseFloat(monthly.total_cost) : 0,
        daily_count: daily ? daily.count : 0,
        weekly_count: weekly ? weekly.count : 0,
        monthly_count: monthly ? monthly.count : 0
      };
    });

    setMultiPeriodCountryData(combined);
  }, [dailyCountryData, weeklyCountryData, monthlyCountryData]);
  
  // Apply filtering to countries when metrics or filter settings change
  useEffect(() => {
    if (!metrics?.requestsByCountry) return;
    
    let filtered = [...metrics.requestsByCountry];
    
    // Sort based on selected filter
    if (countryFilter === 'count') {
      filtered.sort((a, b) => b.count - a.count);
    } else if (countryFilter === 'cost') {
      filtered.sort((a, b) => parseFloat(b.total_cost) - parseFloat(a.total_cost));
    } else if (countryFilter === 'time') {
      filtered.sort((a, b) => parseFloat(b.avg_processing_time) - parseFloat(a.avg_processing_time));
    }
    
    // Limit the number of entries
    filtered = filtered.slice(0, countryLimit);
    
    setFilteredCountries(filtered);
  }, [metrics, countryFilter, countryLimit]);

  // Apply filtering for multi-period country cost chart
  useEffect(() => {
    if (!multiPeriodCountryData || multiPeriodCountryData.length === 0) return;
    
    let filtered = [...multiPeriodCountryData];
    
    // Sort based on selected sort criteria
    filtered.sort((a, b) => {
      switch (countryCostSortBy) {
        case 'daily_cost':
          return b.daily_cost - a.daily_cost;
        case 'weekly_cost':
          return b.weekly_cost - a.weekly_cost;
        case 'monthly_cost':
        default:
          return b.monthly_cost - a.monthly_cost;
      }
    });
    
    // Limit the number of entries
    filtered = filtered.slice(0, countryCostLimit);
    
    setFilteredMultiPeriodCountries(filtered);
  }, [multiPeriodCountryData, countryCostLimit, countryCostSortBy]);

  const ChartCard = ({ title, id, children, className = '', filterComponent }: ChartCardProps) => {
    const isExpanded = expandedCards.has(id);

    const toggleCard = () => {
      const newExpandedCards = new Set(expandedCards);
      if (isExpanded) {
        newExpandedCards.delete(id);
      } else {
        newExpandedCards.add(id);
      }
      setExpandedCards(newExpandedCards);
    };

    return (
      <Card className={`bg-gray-800 text-white ${className}`}>
        <CardHeader 
          className="p-4 lg:p-6 cursor-pointer"
          onClick={toggleCard}
        >
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">{title}</CardTitle>
            <div className="flex items-center gap-2">
              {filterComponent && isExpanded && (
                <div onClick={(e) => e.stopPropagation()} className="mr-2">
                  {filterComponent}
                </div>
              )}
              <ChevronDown 
                className={`w-5 h-5 transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </div>
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="p-4 lg:p-6">
            {children}
          </CardContent>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-4">
        <AlertCircle className="h-5 w-5" />
        <AlertDescription>Error: {error}</AlertDescription>
      </Alert>
    );
  }

  if (!metrics) return null;

  const timeframeMetrics = [
    { label: 'Last Hour', value: metrics.lastHourRequests[0].total, icon: Clock },
    { label: 'Last Day', value: metrics.lastDayRequests[0].total, icon: Clock },
    { label: 'Last Week', value: metrics.lastWeekRequests[0].total, icon: Clock },
    { label: 'Last Month', value: metrics.lastMonthRequests[0].total, icon: Clock }
  ];
  
  // Create country filter dropdown component
  const CountryFilterComponent = () => (
    <div className="flex items-center space-x-2 text-sm">
      <Filter className="w-4 h-4" />
      <select 
        value={countryFilter} 
        onChange={(e) => setCountryFilter(e.target.value)}
        className="bg-gray-700 text-white text-xs rounded px-1 py-1"
      >
        <option value="count">Requests</option>
        <option value="cost">Cost</option>
        <option value="time">Time</option>
      </select>
      <select 
        value={countryLimit} 
        onChange={(e) => setCountryLimit(Number(e.target.value))}
        className="bg-gray-700 text-white text-xs rounded px-1 py-1"
      >
        <option value="5">Top 5</option>
        <option value="10">Top 10</option>
        <option value="20">Top 20</option>
      </select>
      <select 
        value={timePeriod} 
        onChange={(e) => setTimePeriod(e.target.value)}
        className="bg-gray-700 text-white text-xs rounded px-1 py-1"
      >
        <option value="day">Per Day</option>
        <option value="week">Per Week</option>
        <option value="month">Per Month</option>
      </select>
    </div>
  );

  // Create country cost filter dropdown component
  const CountryCostFilterComponent = () => (
    <div className="flex items-center space-x-2 text-sm">
      <Filter className="w-4 h-4" />
      <select 
        value={countryCostLimit} 
        onChange={(e) => setCountryCostLimit(Number(e.target.value))}
        className="bg-gray-700 text-white text-xs rounded px-1 py-1"
      >
        <option value="5">Top 5</option>
        <option value="10">Top 10</option>
        <option value="15">Top 15</option>
        <option value="20">Top 20</option>
      </select>
      <select 
        value={countryCostSortBy} 
        onChange={(e) => setCountryCostSortBy(e.target.value)}
        className="bg-gray-700 text-white text-xs rounded px-1 py-1"
      >
        <option value="daily_cost">Sort by Daily</option>
        <option value="weekly_cost">Sort by Weekly</option>
        <option value="monthly_cost">Sort by Monthly</option>
      </select>
    </div>
  );

  return (
    <div className="p-4 lg:p-8 bg-gray-900 min-h-screen">
      <div className="flex flex-col lg:flex-row lg:justify-between mb-6">
        <div className="flex items-center gap-4 mb-4 lg:mb-0">
          <h1 className="text-xl lg:text-3xl font-bold text-white">Analytics Dashboard</h1>
          <a
            href="/dashboard-process-logs"
            className="px-3 py-1 lg:px-4 lg:py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
          >
            View Logs
          </a>
          <a
            href="/dashboard-promo-codes"
            className="px-3 py-1 lg:px-4 lg:py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
          >
            Promo Codes
          </a>
        </div>
        <div className="text-sm text-gray-400">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 lg:gap-4 mb-4 lg:mb-6">
        {timeframeMetrics.map((metric) => (
          <Card key={metric.label} className="bg-gray-800 text-white">
            <CardContent className="p-2 lg:pt-6 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-gray-400">{metric.label}</p>
                  <p className="text-lg lg:text-2xl font-bold text-blue-400">{metric.value.toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <metric.icon className="w-6 h-6 lg:w-8 lg:h-8 text-gray-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-col space-y-4">
        <ChartCard title="Daily Costs" id="costs">
          <div className="h-80 lg:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics.costByDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9CA3AF"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  interval={isMobile ? 1 : 0}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  wrapperStyle={{ zIndex: 1000 }}
                />
                <Legend />
                <Line type="monotone" dataKey="total_cost" stroke="#60A5FA" name="Cost ($)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard 
          title="Country Costs: Daily vs Weekly vs Monthly" 
          id="country-costs"
          filterComponent={<CountryCostFilterComponent />}
        >
          <div className="h-[32rem] lg:h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={filteredMultiPeriodCountries}
                layout={isMobile ? "vertical" : "horizontal"}
                margin={isMobile ? { left: 50 } : { bottom: 50 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                {isMobile ? (
                  <>
                    <YAxis 
                      dataKey="country" 
                      type="category" 
                      stroke="#9CA3AF"
                      tick={{ fontSize: 12 }}
                      width={40}
                    />
                    <XAxis 
                      type="number" 
                      stroke="#9CA3AF"
                    />
                  </>
                ) : (
                  <>
                    <XAxis 
                      dataKey="country" 
                      stroke="#9CA3AF"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis stroke="#9CA3AF" />
                  </>
                )}
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  wrapperStyle={{ zIndex: 1000 }}
                  formatter={(value, name) => [`$${parseFloat(String(value)).toFixed(2)}`, name]}
                  labelFormatter={(country) => `Country: ${country}`}
                />
                <Legend />
                <Bar 
                  dataKey="daily_cost" 
                  fill="#3B82F6" 
                  name="Daily Cost ($)" 
                />
                <Bar 
                  dataKey="weekly_cost" 
                  fill="#10B981" 
                  name="Weekly Cost ($)" 
                />
                <Bar 
                  dataKey="monthly_cost" 
                  fill="#F59E0B" 
                  name="Monthly Cost ($)" 
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard 
          title="Requests by Country" 
          id="countries"
          filterComponent={<CountryFilterComponent />}
        >
          <div className="h-[32rem] lg:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={filteredCountries}
                layout={isMobile ? "vertical" : "horizontal"}
                margin={isMobile ? { left: 50 } : { bottom: 50 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                {isMobile ? (
                  // Vertical layout for mobile
                  <>
                    <YAxis 
                      dataKey="country" 
                      type="category" 
                      stroke="#9CA3AF"
                      tick={{ fontSize: 12 }}
                      width={40}
                    />
                    <XAxis 
                      type="number" 
                      stroke="#9CA3AF"
                    />
                  </>
                ) : (
                  // Horizontal layout for desktop
                  <>
                    <XAxis 
                      dataKey="country" 
                      stroke="#9CA3AF"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis stroke="#9CA3AF" />
                  </>
                )}
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  wrapperStyle={{ zIndex: 1000 }}
                />
                <Legend />
                {countryFilter === 'count' && (
                  <Bar dataKey="count" fill="#4ADE80" name="Requests" />
                )}
                {countryFilter === 'cost' && (
                  <Bar dataKey="total_cost" fill="#60A5FA" name="Cost ($)" />
                )}
                {countryFilter === 'time' && (
                  <Bar dataKey="avg_processing_time" fill="#F472B6" name="Avg Time (ms)" />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard 
          title="Hourly Statistics" 
          id="hourly"
          filterComponent={
            <div className="flex items-center space-x-2 text-sm">
              <Filter className="w-4 h-4" />
              <select 
                className="bg-gray-700 text-white text-xs rounded px-1 py-1"
                value={hourlyChartType}
                onChange={(e) => setHourlyChartType(e.target.value)}
              >
                <option value="both">Both</option>
                <option value="requests">Requests</option>
                <option value="time">Avg Time</option>
              </select>
            </div>
          }
        >
          <div className="h-[32rem] lg:h-72">
            <ResponsiveContainer width="100%" height="100%">
              {isMobile ? (
                // Vertical layout for mobile with filtered non-zero hours
                <BarChart 
                  data={metrics.hourlyStats.filter(stat => stat.request_count > 0).slice(0, 12)} 
                  layout="vertical"
                  margin={{ left: 35, right: 35 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <YAxis 
                    dataKey="hour" 
                    type="category"
                    tickFormatter={(hour) => `${hour}:00`}
                    stroke="#9CA3AF"
                    tick={{ fontSize: 11 }}
                  />
                  <XAxis type="number" stroke="#9CA3AF" />
                  <Tooltip 
                    formatter={(value, name) => [
                      value, 
                      name === "Requests" ? "Requests" : "Avg Time (ms)"
                    ]}
                    labelFormatter={(hour) => `Hour: ${hour}:00`}
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Legend />
                  {(hourlyChartType === "both" || hourlyChartType === "requests") && (
                    <Bar dataKey="request_count" fill="#60A5FA" name="Requests" />
                  )}
                  {(hourlyChartType === "both" || hourlyChartType === "time") && (
                    <Bar dataKey="avg_processing_time" fill="#FBBF24" name="Avg Time (ms)" />
                  )}
                </BarChart>
              ) : (
                // Original horizontal layout for desktop
                <BarChart data={metrics.hourlyStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="hour" 
                    tickFormatter={(hour) => `${hour}:00`}
                    stroke="#9CA3AF"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis yAxisId="left" stroke="#9CA3AF" />
                  <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" />
                  <Tooltip 
                    formatter={(value, name) => [
                      value, 
                      name === "Requests" ? "Requests" : "Avg Time (ms)"
                    ]}
                    labelFormatter={(hour) => `Hour: ${hour}:00`}
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Legend />
                  {(hourlyChartType === "both" || hourlyChartType === "requests") && (
                    <Bar yAxisId="left" dataKey="request_count" fill="#60A5FA" name="Requests" />
                  )}
                  {(hourlyChartType === "both" || hourlyChartType === "time") && (
                    <Bar yAxisId="right" dataKey="avg_processing_time" fill="#FBBF24" name="Avg Time (ms)" />
                  )}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Top IP Addresses" id="ips">
          <div className="h-80 lg:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={metrics.requestsByIp.slice(0, isMobile ? 5 : 10)}
                layout={isMobile ? "vertical" : "horizontal"}
                margin={isMobile ? { left: 100 } : { bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                {isMobile ? (
                  <>
                    <YAxis 
                      dataKey="ip_address" 
                      type="category"
                      stroke="#9CA3AF"
                      tick={{ fontSize: 11 }}
                      width={90}
                    />
                    <XAxis type="number" stroke="#9CA3AF" />
                  </>
                ) : (
                  <>
                    <XAxis 
                      dataKey="ip_address" 
                      stroke="#9CA3AF"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis stroke="#9CA3AF" />
                  </>
                )}
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  wrapperStyle={{ zIndex: 1000 }}
                />
                <Bar dataKey="count" fill="#34D399" name="Requests" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Requests by Input Type" id="input-types">
          <div className="h-64 lg:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={metrics.requestsByInputType}
                layout={isMobile ? "vertical" : "horizontal"}
                margin={isMobile ? { left: 100 } : {}}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                {isMobile ? (
                  <>
                    <YAxis
                      dataKey="input_type"
                      type="category"
                      stroke="#9CA3AF"
                      tick={{ fontSize: 11 }}
                      width={90}
                    />
                    <XAxis type="number" stroke="#9CA3AF" />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="input_type" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                  </>
                )}
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  wrapperStyle={{ zIndex: 1000 }}
                  formatter={(value, name) => [value, name === 'count' ? 'Requests' : `Cost ($${parseFloat(String(value)).toFixed(4)})`]}
                />
                <Legend />
                <Bar dataKey="count" fill="#A78BFA" name="Requests" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Endpoint Usage" id="endpoints">
          <div className="h-64 lg:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={metrics.requestsByEndpoint}
                layout={isMobile ? "vertical" : "horizontal"}
                margin={isMobile ? { left: 100 } : {}}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                {isMobile ? (
                  <>
                    <YAxis 
                      dataKey="endpoint" 
                      type="category"
                      stroke="#9CA3AF"
                      tick={{ fontSize: 11 }}
                      width={90}
                    />
                    <XAxis type="number" stroke="#9CA3AF" />
                  </>
                ) : (
                  <>
                    <XAxis 
                      dataKey="endpoint" 
                      stroke="#9CA3AF"
                    />
                    <YAxis stroke="#9CA3AF" />
                  </>
                )}
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  wrapperStyle={{ zIndex: 1000 }}
                />
                <Bar dataKey="count" fill="#F472B6" name="Requests" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Avg Processing Time" id="processing">
          <div className="h-64 lg:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={metrics.requestsByEndpoint}
                layout={isMobile ? "vertical" : "horizontal"}
                margin={isMobile ? { left: 100 } : {}}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                {isMobile ? (
                  <>
                    <YAxis 
                      dataKey="endpoint" 
                      type="category"
                      stroke="#9CA3AF"
                      tick={{ fontSize: 11 }}
                      width={90}
                    />
                    <XAxis type="number" stroke="#9CA3AF" />
                  </>
                ) : (
                  <>
                    <XAxis 
                      dataKey="endpoint" 
                      stroke="#9CA3AF"
                    />
                    <YAxis stroke="#9CA3AF" />
                  </>
                )}
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  wrapperStyle={{ zIndex: 1000 }}
                />
                <Bar dataKey="avg_time" fill="#FBBF24" name="Time (ms)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default Dashboard;